import { useCallback, useEffect, useState } from 'react';

const adminApiBaseUrl = (
  process.env.REACT_APP_PAYMENT_API_URL ||
  'https://southamerica-east1-coloniadeferiasss.cloudfunctions.net/paymentApi'
).replace(/\/$/, '');

export const categoriasGasto = [
  'Camisas',
  'Alimentação',
  'Material',
  'Passeio',
  'Equipe',
  'Estrutura',
  'Outros'
];

export interface Gasto {
  id: string;
  periodo: string;
  descricao: string;
  categoria: string;
  quantidade: number;
  valorUnitario: number;
  data: string;
}

export type NovoGasto = Omit<Gasto, 'id'>;

export const totalDoGasto = (gasto: Pick<Gasto, 'quantidade' | 'valorUnitario'>) =>
  gasto.quantidade * gasto.valorUnitario;

const normalizeGasto = (raw: Record<string, unknown>): Gasto | null => {
  const descricao = String(raw.descricao || '').trim();
  const id = String(raw.id || '').trim();
  if (!descricao || !id) return null;

  const quantidade = Number(raw.quantidade);
  const valorUnitario = Number(raw.valorUnitario);

  return {
    id,
    periodo: String(raw.periodo || '').trim(),
    descricao,
    categoria: String(raw.categoria || 'Outros').trim() || 'Outros',
    quantidade: Number.isFinite(quantidade) ? quantidade : 0,
    valorUnitario: Number.isFinite(valorUnitario) ? valorUnitario : 0,
    data: String(raw.data || '').trim()
  };
};

export const fetchGastos = async (periodo: string): Promise<Gasto[]> => {
  const response = await fetch(`${adminApiBaseUrl}/admin/gastos?periodo=${encodeURIComponent(periodo)}`);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) {
    throw new Error(payload.error || 'Não foi possível carregar os gastos.');
  }
  const lista: unknown[] = Array.isArray(payload.gastos) ? payload.gastos : [];
  return lista
    .map((item) => (item && typeof item === 'object' ? normalizeGasto(item as Record<string, unknown>) : null))
    .filter((item): item is Gasto => Boolean(item))
    .sort((a, b) => (b.data || '').localeCompare(a.data || ''));
};

export const createGasto = async (gasto: NovoGasto): Promise<Gasto> => {
  const response = await fetch(`${adminApiBaseUrl}/admin/gastos`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(gasto)
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) {
    throw new Error(payload.error || 'Não foi possível salvar o gasto.');
  }
  const criado = payload.gasto && typeof payload.gasto === 'object' ? normalizeGasto(payload.gasto) : null;
  if (!criado) throw new Error('Resposta inválida ao salvar o gasto.');
  return criado;
};

export const deleteGasto = async (id: string) => {
  const response = await fetch(`${adminApiBaseUrl}/admin/gastos/${encodeURIComponent(id)}`, { method: 'DELETE' });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) {
    throw new Error(payload.error || 'Não foi possível excluir o gasto.');
  }
};

export const useGastos = (periodo: string) => {
  const [gastos, setGastos] = useState<Gasto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const recarregar = useCallback(() => {
    let active = true;
    setLoading(true);
    fetchGastos(periodo)
      .then((data) => {
        if (!active) return;
        setGastos(data);
        setError('');
      })
      .catch((loadError: Error) => {
        if (!active) return;
        setGastos([]);
        setError(loadError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [periodo]);

  useEffect(() => recarregar(), [recarregar]);

  return { gastos, loading, error, setGastos, recarregar };
};
